import { useMutation, useQuery } from '@tanstack/react-query';

import type {
  CreateServiceRequestDto,
  CreateServiceRequestResult,
  PublicBadge,
  PublicServiceRequest,
} from '@repo/contracts';

import { apiFetch } from './client';

export const serviceKeys = {
  badge: (id: string) => ['services', 'badge', id] as const,
  request: (token: string) => ['services', 'request', token] as const,
};

/**
 * The intake form's payload. A type, not the DTO class: importing the class
 * would pull class-validator's decorators into the bundle.
 */
export type ServiceRequestInput = CreateServiceRequestDto;

export function createServiceRequest(
  input: ServiceRequestInput,
): Promise<CreateServiceRequestResult> {
  return apiFetch('/services/requests', {
    body: JSON.stringify(input),
    method: 'POST',
  });
}

export function uploadServiceDocument(
  statusToken: string,
  file: File,
): Promise<unknown> {
  const body = new FormData();
  body.append('file', file);
  return apiFetch(
    `/services/requests/${encodeURIComponent(statusToken)}/documents`,
    { body, method: 'POST' },
  );
}

export interface SubmitResult {
  reference: string;
  statusToken: string;
  /** Files that did not upload — the request itself still went through. */
  failedUploads: string[];
}

/** Create the request, then attach each document. A failed upload doesn't
 *  undo the request: the customer is told which files to send by email. */
export function useSubmitServiceRequest() {
  return useMutation({
    mutationFn: async ({
      files,
      input,
    }: {
      files: File[];
      input: ServiceRequestInput;
    }): Promise<SubmitResult> => {
      const created = await createServiceRequest(input);
      const failedUploads: string[] = [];
      for (const file of files) {
        try {
          await uploadServiceDocument(created.statusToken, file);
        } catch {
          failedUploads.push(file.name);
        }
      }
      return { ...created, failedUploads };
    },
  });
}

export function useServiceRequest(token: string) {
  return useQuery({
    queryFn: () =>
      apiFetch<PublicServiceRequest>(
        `/services/requests/${encodeURIComponent(token)}`,
      ),
    queryKey: serviceKeys.request(token),
  });
}

export function useBadge(id: string) {
  return useQuery({
    queryFn: () =>
      apiFetch<PublicBadge>(`/services/badges/${encodeURIComponent(id)}`),
    queryKey: serviceKeys.badge(id),
  });
}
